import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { getBackendUrl } from "../../lib/utils";

const BACKEND = getBackendUrl();
const API = `${BACKEND}/api`;

const TIERS = [
  { id: "neural-starter", name: "Neural Starter", price: "$9", cadence: "/ month", tagline: "Begin a living memory.",
    features: ["1 language · full memory depth", "Voice tutoring · 120 min/mo", "Goal-locked curriculum", "Weekly CEFR drift"],
    popular: false },
  { id: "neural-pro", name: "Neural Pro", price: "$24", cadence: "/ month", tagline: "For serious learners.",
    features: ["3 languages · unified memory", "Unlimited voice tutoring", "Exam mode (DELF, DELE, TOEFL, IELTS)", "Daily adaptive sessions", "Private voice print"],
    popular: true },
  { id: "neural-fluent", name: "Neural Fluent", price: "$54", cadence: "/ month", tagline: "For those who live in two worlds.",
    features: ["All languages · lifetime memory", "Priority latency (0.2s)", "Relocation & business tracks", "Fluency coach reviews", "API · export your memory"],
    popular: false },
];

const PricingWaitlist = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState("Spanish");
  const [level, setLevel] = useState("B1");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then((r) => setCount(r.data.display_count)).catch(() => setCount(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
    if (!emailOk) { setStatus({ type: "error", message: "Please enter a valid email address." }); return; }
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/waitlist`, { email: value, target_language: lang, current_level: level });
      setStatus({
        type: "ok",
        message: res.data.status === "already_joined" ? "You're already on the list." : "You're in. AURA will remember you.",
        position: res.data.position,
      });
      setEmail("");
      if (typeof res.data.position === "number") setCount((c) => (c ? c + 1 : null));
      // Smooth redirect to onboarding so the user can experience the dashboard
      setTimeout(() => nav("/onboarding"), 1100);
    } catch (err) {
      setStatus({ type: "error", message: "Something went wrong. Try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pricing" className="relative py-28 px-6" data-testid="section-pricing">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">Pricing · waitlist</div>
        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 className="headline text-4xl sm:text-5xl lg:text-[72px] max-w-3xl">
            Premium. <em>Simple.</em>
          </h2>
          <p className="text-whisper text-[16px] leading-[1.55] max-w-md">
            Founding members lock their price for life.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-4">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className={`relative rounded-3xl p-7 overflow-hidden ${t.popular ? "node-card edge-glow" : "glass hairline"}`}
              data-testid={`pricing-tier-${t.id}`}
            >
              {t.popular && (<div className="absolute top-4 right-4 pill text-[10px]">Most chosen</div>)}
              <div className="relative z-10">
                <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">{t.tagline}</div>
                <h3 className="mt-3 font-display text-[28px] font-light tracking-tight text-ink">{t.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <div className="font-display text-[54px] font-light leading-none tracking-tighter text-ink">{t.price}</div>
                  <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-whisper-muted pb-2">{t.cadence}</div>
                </div>
                <ul className="mt-6 space-y-2.5 text-[14.5px] text-whisper">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="mt-1 text-aura-cyan" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="#waitlist" className={`mt-7 inline-flex ${t.popular ? "btn-primary" : "btn-ghost"}`} data-testid={`pricing-cta-${t.id}`}>
                  Reserve {t.name}
                  <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.form
          id="waitlist"
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14 relative rounded-3xl glass edge-glow p-6 sm:p-10 overflow-hidden"
          data-testid="waitlist-form"
        >
          <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-aura-cyan/15 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-6">
              <div className="section-label">Join the waitlist</div>
              <h3 className="mt-3 font-display text-[30px] sm:text-[40px] font-light tracking-tight text-ink leading-[1.05]">
                Be among the first AURA ever remembers.
              </h3>
              {typeof count === 'number' && (<div className="mt-3 font-mono text-[11px] tracking-[0.22em] uppercase text-whisper-muted">{count.toLocaleString()} already reserved</div>)}
            </div>
            <div className="lg:col-span-6">
              <div className="grid sm:grid-cols-[1fr_auto] gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  className="w-full bg-panel/[0.02] border border-panel/10 rounded-full px-5 py-4 text-[15px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60 focus:ring-[3px] focus:ring-aura-cyan/10"
                  data-testid="waitlist-input-email"
                />
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="waitlist-submit-button">
                  {loading ? (<><Loader2 size={14} className="animate-spin" /> saving…</>) : (<>Join waitlist <ArrowRight size={14} /></>)}
                </button>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <label className="relative">
                  <span className="sr-only">Language</span>
                  <select value={lang} onChange={(e) => setLang(e.target.value)}
                    className="w-full appearance-none bg-panel/[0.02] border border-panel/10 rounded-full px-5 py-3 text-[13.5px] text-ink focus:outline-none focus:border-aura-cyan/60"
                    data-testid="waitlist-select-language">
                    {["Spanish", "French", "German", "Italian", "Portuguese", "Japanese", "Mandarin", "Arabic", "Korean", "Hindi"].map((l) => <option key={l}>{l}</option>)}
                  </select>
                </label>
                <label className="relative">
                  <span className="sr-only">Level</span>
                  <select value={level} onChange={(e) => setLevel(e.target.value)}
                    className="w-full appearance-none bg-panel/[0.02] border border-panel/10 rounded-full px-5 py-3 text-[13.5px] text-ink focus:outline-none focus:border-aura-cyan/60"
                    data-testid="waitlist-select-level">
                    {["A1", "A2", "B1", "B2", "C1", "C2"].map((l) => <option key={l}>{l}</option>)}
                  </select>
                </label>
              </div>
              {status && (
                <div className={`mt-4 text-[13.5px] ${status.type === "ok" ? "text-aura-cyan" : "text-red-300"}`} data-testid="waitlist-status">
                  {status.message}
                  {status.type === "ok" && typeof status.position === "number" && (<span className="ml-2 text-whisper-muted">· position #{status.position}</span>)}
                </div>
              )}
              <div className="mt-3 text-whisper-muted text-[12px]">Free trial. No credit card. Your memory stays yours.</div>
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default PricingWaitlist;
